import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CreateProjectService } from '../services/create-project.service';
import { ManageCollectionsService } from '../services/manage-collections.service';
import { RoleViewService } from '../services/role-view.service';
import { NotificationService } from '../services/notification.service';
import { TutorialVideoStep, TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';

type PreviewFileType = 'pdf' | 'docx' | 'xls' | 'image' | 'unsupported';

interface DocumentRow {
  id: string;
  name: string;
  uploadedBy: string;
  dateModified: string;
  isSample?: boolean;
  is_deleted?: boolean;
}

interface CollectionRow {
  id: string;
  name: string;
  documents: DocumentRow[];
  archivedDocuments: DocumentRow[];
  isExpanded: boolean;
  isLoading: boolean;
  uploadInProgress: boolean;
}

@Component({
  selector: 'app-manage-document',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatExpansionModule, TutorialVideoWidgetComponent],
  templateUrl: './manage-document.component.html',
  styleUrl: './manage-document.component.css'
})
export class ManageDocumentComponent implements OnInit {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  collectionRows: CollectionRow[] = [];
  searchQuery = '';
  isLoading = false;
  scrHeight = '';
  workDayId = '';
  userRole = '';
  userName = '';
  showArchived = false;
  showUploadModal = false;
  selectedUploadCollectionId = '';
  selectedUploadFiles: File[] = [];
  isSubmittingUpload = false;
  previewTitle = '';
  previewUrl: string | null = null;
  previewType: PreviewFileType | null = null;
  isPreviewOpen = false;
  @ViewChild('docxContainer') docxContainerRef!: ElementRef<HTMLDivElement>;
  workbookCache: any = null;
  sheetNames: string[] = [];
  selectedSheetIndex = 0;
  excelHtml = '';
  // Delete confirmation state
  pendingDeleteDoc: DocumentRow | null = null;
  pendingDeleteCollection: CollectionRow | null = null;
  showDeleteConfirm = false;

  private readonly ERROR_OPEN_DOCUMENT = 'Unable to open document';

  private readonly MIME_TYPES: { [key: string]: string } = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xlsm: 'application/vnd.ms-excel.sheet.macroenabled.12',
    csv: 'text/csv',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png'
  };

  private readonly ALLOWED_FILE_TYPES: string[] = Object.values(this.MIME_TYPES);
  private readonly ALLOWED_EXTENSIONS: string[] = Object.keys(this.MIME_TYPES);

  constructor(
    private readonly createProjectService: CreateProjectService,
    private readonly manageCollectionsService: ManageCollectionsService,
    private  readonly roleViewService: RoleViewService,
    private readonly notificationService: NotificationService
  ) { }

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: any): void {
    this.scrHeight = `${window.innerHeight - 90}px`;
  }

  ngOnInit(): void {
    this.getScreenSize();
    this.getUserInfo();
    this.loadCollections();
  }

  getUserInfo(): void {
    this.workDayId = this.roleViewService.getWorkdayId() || '';
    this.userRole = this.roleViewService.getActualRole();
    this.userName = this.roleViewService.getUserName();
  }

  get isAdmin(): boolean {
    return this.userRole === 'superAdmin';
  }

  get totalCollections(): number {
    return this.collectionRows.length;
  }

  get totalDocuments(): number {
    return this.collectionRows.reduce((sum, c) => sum + c.documents.length, 0);
  }

  get totalArchivedDocuments(): number {
    return this.collectionRows.reduce((sum, c) => sum + c.archivedDocuments.length, 0);
  }

  get totalArchivedCollections(): number {
    return this.collectionRows.filter(col => col.archivedDocuments.length > 0).length;
  }

  get hiddenEmptyCollectionsCount(): number {
    return this.collectionRows.filter(col => col.documents.length === 0).length;
  }

  filteredCollections(): CollectionRow[] {
    const q = this.searchQuery.trim().toLowerCase();
    return this.collectionRows.filter(col => {
      const hasDocuments = col.documents.length > 0;
      if (!hasDocuments) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (
        col.name.toLowerCase().includes(q) ||
        col.documents.some(d =>
          d.name.toLowerCase().includes(q) ||
          d.uploadedBy.toLowerCase().includes(q)
        )
      );
    });
  }

  filteredArchivedCollections(): CollectionRow[] {
    const q = this.searchQuery.trim().toLowerCase();
    return this.collectionRows.filter(col => {
      const hasDocuments = col.archivedDocuments.length > 0;
      if (!hasDocuments) {
        return false;
      }
      if (!q) {
        return true;
      }
      return (
        col.name.toLowerCase().includes(q) ||
        col.archivedDocuments.some(d =>
          d.name.toLowerCase().includes(q) ||
          d.uploadedBy.toLowerCase().includes(q)
        )
      );
    });
  }

  get uploadCollections(): CollectionRow[] {
    return this.collectionRows;
  }

  loadCollections(): void {
    if (!this.workDayId) {
      this.notificationService.showError('Unable to load collections: Missing WorkdayID');
      return;
    }
    this.isLoading = true;
    this.manageCollectionsService.fetchCollections(this.workDayId).subscribe({
      next: (response: any) => {
        const apiCollections = response?.payload?.response || response?.payload || response || [];
        const activeCollections: CollectionRow[] = apiCollections
          .filter((col: any) => !col.is_deleted)
          .map((col: any): CollectionRow => ({
            id: col.collection_id,
            name: col.collection_name,
            documents: [],
            archivedDocuments: [],
            isExpanded: false,
            isLoading: false,
            uploadInProgress: false
          }));

        this.collectionRows = activeCollections;
        this.isLoading = false;

        if (activeCollections.length === 0) {
          return;
        }

        // Fetch all documents in a single API call without collectionId parameter
        this.createProjectService.fetchDocument().pipe(catchError(() => of(null))).subscribe((res: any) => {
          const allDocs = res?.payload || res?.data || res || [];
          const docs = Array.isArray(allDocs) ? allDocs : [];
          // Map all documents to their respective collections
          this.collectionRows.forEach(col => {
            const collectionDocs = docs.filter((d: any) =>
              String(d.collection_id) === String(col.id) || String(d.collectionId) === String(col.id)
            );
            const mappedDocs = collectionDocs.map((d: any): DocumentRow => this.mapDocument(d));

            // Separate active and archived documents based on is_deleted flag
            col.documents = mappedDocs.filter(d => !d.is_deleted);
            col.archivedDocuments = mappedDocs.filter(d => d.is_deleted);
          });

        });
      },
      error: () => {
        this.isLoading = false;
        this.notificationService.showError('Failed to load collections');
      }
    });
  }

  private mapDocument(d: any): DocumentRow {
    return {
      id: d.id || d.cd_id || d.file_versioning_id || '',
      name: d.name || d.document_name || d.file_name || 'Untitled',
      uploadedBy: d.owner || d.uploaded_by || d.created_by || '-',
      dateModified: d.dateModified || d.uploaded_at || d.created_at || '',
      is_deleted: d.is_deleted || false,
      isSample: false
    };
  }

  setArchivedView(showArchived: boolean): void {
    this.showArchived = showArchived;
  }

  toggleExpand(col: CollectionRow): void {
    col.isExpanded = !col.isExpanded;
  }

  // ---- Upload ----
  openUploadModal(): void {
    this.showUploadModal = true;
    this.selectedUploadCollectionId = '';
    this.selectedUploadFiles = [];
    this.isSubmittingUpload = false;
  }

  closeUploadModal(): void {
    this.showUploadModal = false;
    this.selectedUploadCollectionId = '';
    this.selectedUploadFiles = [];
    this.isSubmittingUpload = false;
  }

  private isAllowedUploadFileType(file: File): boolean {
    const type = (file.type || '').toLowerCase();
    if (type && this.ALLOWED_FILE_TYPES.includes(type)) {
      return true;
    }
    // Fallback by extension since browsers don't reliably report MIME types
    // for some formats (e.g. .xlsm is often reported blank or as a generic type).
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    return this.ALLOWED_EXTENSIONS.includes(ext);
  }

  onUploadFileSelected(event: Event, fileInput: HTMLInputElement): void {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      return;
    }

    const validFiles: File[] = [];
    for (const file of Array.from(files)) {
      if (!this.isAllowedUploadFileType(file)) {
        this.notificationService.showError(`Skipped "${file.name}" - unsupported file type`);
      } else if (this.selectedUploadFiles.some(f => f.name === file.name && f.size === file.size)) {
        this.notificationService.showError(`"${file.name}" is already selected`);
      } else {
        validFiles.push(file);
      }
    }

    this.selectedUploadFiles.push(...validFiles);
    if (validFiles.length > 0) {
      this.notificationService.showSuccess(`Added ${validFiles.length} file(s)`);
    }
    fileInput.value = '';
  }

  submitUpload(fileInput: HTMLInputElement): void {
    if (!this.selectedUploadCollectionId) {
      this.notificationService.showError('Please select a collection');
      return;
    }
    if (this.selectedUploadFiles.length === 0) {
      this.notificationService.showError('Please choose at least one document to upload');
      return;
    }

    const collection = this.collectionRows.find(col => String(col.id) === this.selectedUploadCollectionId);
    console.info(collection);
    if (!collection) {
      this.notificationService.showError('Selected collection not found');
      return;
    }

    this.isSubmittingUpload = true;
    collection.uploadInProgress = true;
    let successCount = 0;
    let failureCount = 0;

    const uploadNext = (index: number) => {
      if (index >= this.selectedUploadFiles.length) {
        // All uploads complete
        collection.uploadInProgress = false;
        this.isSubmittingUpload = false;
        fileInput.value = '';

        if (failureCount === 0 && successCount > 0) {
          this.notificationService.showSuccess(`${successCount} document(s) uploaded successfully`);
          this.closeUploadModal();
          this.loadCollections();
        } else if (successCount > 0 && failureCount > 0) {
          this.notificationService.showSuccess(`${successCount} uploaded, ${failureCount} failed`);
          this.closeUploadModal();
          this.loadCollections();
        } else if (failureCount > 0) {
          this.notificationService.showError(`Failed to upload ${failureCount} document(s)`);
        }
        return;
      }

      const file = this.selectedUploadFiles[index];
      const reader = new FileReader();

      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const payload = {
          collectionId: collection.id,
          collectionName: collection.name,
          fileName: file.name,
          fileBuffer: base64,
          contentType: file.type,
          uploadedBy: this.userName
        };
        this.createProjectService.collectionDocumentUpload(payload).subscribe({
          next: () => {
            successCount++;
            uploadNext(index + 1);
          },
          error: () => {
            failureCount++;
            uploadNext(index + 1);
          }
        });
      };

      reader.onerror = () => {
        failureCount++;
        uploadNext(index + 1);
      };

      reader.readAsDataURL(file);
    };

    collection.isExpanded = true;
    uploadNext(0);
  }

  private uploadDocumentToCollection(file: File, col: CollectionRow, fileInput: HTMLInputElement, closeModalOnSuccess: boolean): void {
    col.uploadInProgress = true;
    this.isSubmittingUpload = true;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      const payload = {
        collectionId: col.id,
        fileName: file.name,
        fileContent: base64,
        contentType: file.type,
        uploadedBy: this.userName
      };
      this.createProjectService.uploadDocument(this.workDayId, payload).subscribe({
        next: (res: any) => {
          col.uploadInProgress = false;
          this.isSubmittingUpload = false;
          fileInput.value = '';
          const newDoc = res?.payload || res?.data || res;
          if (newDoc && (newDoc.id || newDoc.document_id || newDoc.file_versioning_id)) {
            col.documents.unshift(this.mapDocument(newDoc));
          } else {
            this.reloadCollectionDocuments(col);
          }
          col.isExpanded = true;
          if (closeModalOnSuccess) {
            this.closeUploadModal();
          }
          this.notificationService.showSuccess('Document uploaded successfully');
        },
        error: () => {
          col.uploadInProgress = false;
          this.isSubmittingUpload = false;
          fileInput.value = '';
          this.notificationService.showError('Failed to upload document');
        }
      });
    };
    reader.readAsDataURL(file);
  }

  private reloadCollectionDocuments(col: CollectionRow): void {
    // Fetch all documents and filter for this collection
    this.createProjectService.fetchDocument().pipe(catchError(() => of(null))).subscribe((res: any) => {
      const allDocs = res?.payload || res?.data || res || [];
      const docs = Array.isArray(allDocs) ? allDocs : [];

      // Filter documents for this specific collection
      const collectionDocs = docs.filter((d: any) =>
        String(d.collection_id) === String(col.id) || String(d.collectionId) === String(col.id)
      );
      const mappedDocs = collectionDocs.map((d: any) => this.mapDocument(d));

      // Separate active and archived documents based on is_deleted flag
      col.documents = mappedDocs.filter(d => !d.is_deleted);
      col.archivedDocuments = mappedDocs.filter(d => d.is_deleted);
    });
  }

  // ---- Delete ----
  confirmDelete(doc: DocumentRow, col: CollectionRow): void {
    this.pendingDeleteDoc = doc;
    this.pendingDeleteCollection = col;
    this.showDeleteConfirm = true;
  }

  cancelDelete(): void {
    this.pendingDeleteDoc = null;
    this.pendingDeleteCollection = null;
    this.showDeleteConfirm = false;
  }

  executeDelete(): void {
    if (!this.pendingDeleteDoc || !this.pendingDeleteCollection) {
      return;
    }
    const doc = this.pendingDeleteDoc;
    const col = this.pendingDeleteCollection;
    this.showDeleteConfirm = false;
    this.pendingDeleteDoc = null;
    this.pendingDeleteCollection = null;

    if (doc.isSample) {
      col.documents = col.documents.filter(d => d.id !== doc.id);
      col.archivedDocuments.unshift({ ...doc });
      this.notificationService.showSuccess('Document archived successfully');
      return;
    }

    const payload = {
      cd_id: doc.id
    };
    this.createProjectService.collectionDocumentDelete(payload).subscribe({
      next: () => {
        col.documents = col.documents.filter(d => d.id !== doc.id);
        col.archivedDocuments.unshift({ ...doc });
        this.notificationService.showSuccess('Document archived successfully');
      },
      error: () => {
        this.notificationService.showError('Failed to archive document');
      }
    });
  }

  restoreDocument(doc: DocumentRow, col: CollectionRow): void {
    if (!doc || !col) {
      return;
    }

    col.isExpanded = true;
    const payload = {
      cd_id: doc.id
    };
    this.createProjectService.collectionDocumentDelete(payload).subscribe({
      next: () => {
        col.archivedDocuments = col.archivedDocuments.filter(item => item.id !== doc.id);
        col.documents.unshift({ ...doc });
        this.notificationService.showSuccess('Document restored successfully');
      },
      error: () => {
        this.notificationService.showError('Failed to restore document');
      }
    });
  }

  async viewDocument(doc: DocumentRow): Promise<void> {
    if (!doc){return};

    if (doc.isSample) {
      this.openSamplePreview(doc);
      return;
    }

    if (!this.workDayId || !doc.id) {
      this.notificationService.showError(this.ERROR_OPEN_DOCUMENT);
      return;
    }

    try {
      const res: any = await new Promise((resolve, reject) => {
        this.createProjectService
          .readCollectionDocument(doc.id)
          .subscribe({ next: resolve, error: reject });
      });

      const buffer = await this.getDocumentArrayBuffer(res);
      if (!buffer) {
        this.notificationService.showError(this.ERROR_OPEN_DOCUMENT);
        return;
      }

      const name = (doc.name || '').toLowerCase();

      // ✅ PDF
      if (name.endsWith('.pdf')) {
        window.open(URL.createObjectURL(new Blob([buffer], { type: this.MIME_TYPES.pdf })), '_blank');
        return;
      }

      // ✅ IMAGE
      if (/\.(png|jpg|jpeg)$/.test(name)) {
        const mime = name.endsWith('.png') ? this.MIME_TYPES.png : this.MIME_TYPES.jpeg;
        this.previewTitle = doc.name;
        this.previewType = 'image';
        this.previewUrl = URL.createObjectURL(new Blob([buffer], { type: mime }));
        this.isPreviewOpen = true;
        return;
      }

      // ✅ DOCX
      if (name.endsWith('.docx')) {
        this.previewTitle = doc.name;
        this.previewType = 'docx';
        this.isPreviewOpen = true;

        setTimeout(async () => {
          const container = this.docxContainerRef?.nativeElement;
          if (!container) {return};

          container.innerHTML = '';

          try {
            await renderAsync(buffer, container, undefined, { className: 'docx' });
          } catch (err) {
            console.trace('DOCX render failed', err);
          }
        });

        return;
      }

      // ✅ XLS / XLSM / CSV
      if (/\.(xlsx|xls|xlsm|csv)$/.test(name)) {
        this.previewTitle = doc.name;
        this.previewType = 'xls';
        this.isPreviewOpen = true;

        try {
          const workbook = name.endsWith('.csv')
            ? XLSX.read(new TextDecoder().decode(buffer), { type: 'string' })
            : XLSX.read(buffer, { type: 'array' });

          this.workbookCache = workbook;
          this.sheetNames = workbook.SheetNames || [];
          this.selectedSheetIndex = 0;
          this.renderSheetByIndex(0);
        } catch (err) {
          console.trace('Spreadsheet render failed', err);
        }

        return;
      }

      // ✅ Default
      window.open(
        URL.createObjectURL(new Blob([buffer], { type: this.getMimeType(doc.name) })),
        '_blank'
      );

    } catch (err) {
      console.trace('Error opening document:', err);
      this.notificationService.showError(this.ERROR_OPEN_DOCUMENT);
    }
  }

  closePreview(): void {
    this.isPreviewOpen = false;
    this.previewType = null;
    this.previewUrl = null;
    this.excelHtml = '';
    this.workbookCache = null;
  }

  renderSheetByIndex(index: number) {
    try {
      if (!this.workbookCache?.SheetNames || !this.workbookCache?.Sheets) {
        this.excelHtml = '<div class="xlsx-message">No workbook loaded.</div>';
        return;
      }
      const name = this.workbookCache.SheetNames[index];
      if (!name) {
        this.excelHtml = '<div class="xlsx-message">Sheet not found.</div>';
        return;
      }
      const sheet = this.workbookCache.Sheets[name];
      const tableHtml = XLSX.utils.sheet_to_html(sheet);
      const wrapped = `<div class="xlsx-preview">${tableHtml}</div>`;
      this.excelHtml = wrapped;
    } catch (err) {
      console.trace('renderSheetByIndex failed', err);
      this.excelHtml = '<div class="xlsx-message">Unable to render sheet.</div>';
    }
  }

  private async getDocumentArrayBuffer(res: any): Promise<ArrayBuffer | null> {
    const body = res?.body ?? res?.payload?.body ?? res?.payload ?? res;
    if (!body) {
      return null;
    }

    // URL case
    if (typeof body === 'string' && body.startsWith('http')) {
      const response = await fetch(body);
      if (!response.ok) {
        return null;
      }
      return response.arrayBuffer();
    }

    // Base64 case
    if (typeof body === 'string') {
      const base64 = body.includes(',') ? body.split(',')[1] : body;
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.codePointAt(i) || 0;
      }

      return bytes.buffer;
    }

    return null;
  }

  private openSamplePreview(doc: DocumentRow): void {
    const mimeType = this.getMimeType(doc.name);
    const sampleContent = `Sample preview for ${doc.name}\nUploaded by: ${doc.uploadedBy}\nModified: ${doc.dateModified}`;
    const blob = new Blob([sampleContent], { type: mimeType === this.MIME_TYPES.pdf ? 'text/plain' : mimeType });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  private getMimeType(fileName: string): string {
    const extension = (fileName.split('.').pop() || '').toLowerCase();
    return this.MIME_TYPES[extension] || 'application/octet-stream';
  }

  // ---- Utility ----
  iconType(fileName: string): string {
    if (!fileName) {
      return 'default';
    }
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconMap: { [key: string]: string } = {
      'pdf': 'pdf',
      'doc': 'doc',
      'docx': 'doc',
      'xlsx': 'xls',
      'csv': 'csv',
      'xls': 'xls',
      'xlsm': 'xls',
      // 'pptx': 'pptx',
      'jpg': 'image',
      'jpeg': 'image',
      'png': 'image'
    };
    return iconMap[ext] || 'default';
  }

  // formatDate(dateStr: string): string {
  //   if (!dateStr) {
  //     return '-';
  //   }
  //   const date = new Date(dateStr);
  //   if (Number.isNaN(date.getTime())) {
  //     return dateStr;
  //   }
  //   return date.toLocaleDateString('en-US', {
  //     month: 'short',
  //     day: 'numeric',
  //     year: 'numeric',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     second: '2-digit'
  //   });
  // }


  getLatestDate(documents: { dateModified: string }[]): string {
    if (!documents?.length) {
      return '';
    }
    return documents.reduce((latest, doc) => {
      const t = new Date(doc.dateModified).getTime();
      return t > new Date(latest).getTime() ? doc.dateModified : latest;
    }, documents[0].dateModified);
  }
  

  removeFile(index: number): void {
    this.selectedUploadFiles.splice(index, 1);
    if (this.selectedUploadFiles.length === 0) {
      this.notificationService.showSuccess('File removed');
    }
  }

  clearAllFiles(): void {
    this.selectedUploadFiles = [];
    this.notificationService.showSuccess('All files cleared');
  }

  getFileIcon(filename: string): string {
    const ext = (filename.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'picture_as_pdf';
      case 'doc':
      case 'docx': return 'description';
      case 'xls':
      case 'xlsx':
      case 'xlsm': return 'table_chart';
      case 'csv': return 'grid_on';
      // case 'pptx':
      // case 'ppt': return 'slideshow';
      case 'png':
      case 'jpg':
      case 'jpeg': return 'image';
      default: return 'file_present';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 10) / 10} ${sizes[i]}`;
  }
}
